import { billDiscountLine } from "@/modules/pos/lib/billDiscount";
import { useI18n, useTranslate } from "@/lib/i18n";
import type { Locale } from "@/lib/i18n/config";
import type { TranslateFn } from "@/lib/i18n/intl";
import type { PaperWidth, PrintHardwareActions } from "@/lib/printing/types";
import { receiptFooterForLocale } from "../lib/receiptFooter";
import { moneyFormatter, useMoney } from "@/shared/ui";
import { translatorFor } from "@/lib/i18n/intl";
import { useStoreLogo } from "@/shared/useStoreLogo";
import { tenderMethodsLabel } from "@/lib/settlementMethodLabel";
import { groupPlates, liveOpenTotals } from "@/modules/pos/lib/groupPlates";
import { listSubtotal, netBeforeCharges } from "@/modules/pos/lib/pricing";
import { billCustomerLines, channelLabel } from "@/lib/invoice/place";
import {
  firstAddressLine,
  riderCollect,
} from "@/lib/invoice/paperLines";
import type { Invoice, RestaurantSettings } from "@/lib/api/types";
import {
  PaperHead,
  PaperFooter,
  PaperItems,
  PaperSumRow,
  PaperTotal,
  orderFact,
  paperStamp,
  peopleFacts,
  type PaperFact,
  type PaperItem,
} from "@/modules/printing/paperLayout";
import {
  renderStandalonePrintArtifact,
  type PrintArtifactAction,
  type StandalonePrintArtifact,
} from "@/modules/printing/renderDocument";

/**
 * The 80 mm thermal receipt.
 *
 * Rendered into the page always and revealed only by the print stylesheet, so
 * `window.print()` needs no round-trip, no popup and no second data fetch — on
 * a till, the gap between tapping Print and paper appearing is the whole
 * experience.
 *
 * The browser-print path. (~~No ESC/POS and no silent printing~~ — superseded:
 * a paired print computer prints the same document silently through QZ Tray as
 * a raster image, `modules/printing`; this component is still what
 * `window.print()` reveals when no printer is paired. B148, 2026-10-14.)
 *
 * Voided lines are shown struck through rather than removed: a customer
 * querying their bill is usually asking about something they saw arrive and
 * then not get charged for, and a receipt that simply omits it cannot answer
 * them.
 */
export function Receipt({
  invoice,
  settings,
  tableName,
  platformName = null,
}: {
  invoice: Invoice;
  settings: RestaurantSettings | null;
  /** T19 — the platform's NAME for the off-premise band; null on a direct order. */
  platformName?: string | null;
  /**
   * The table this bill belongs to, as the floor names it ("طاولة ٣").
   *
   * On the CHEQUE — the copy printed before payment and left on the table —
   * this is the line that matters most: a cashier holding four unpaid cheques
   * has no other way to tell which one belongs to which table, and the daily
   * sequence means nothing to the customer reading it. `null` when the floor
   * grid has not loaded, in which case the line is simply omitted rather than
   * printed empty.
   */
  tableName: string | null;
}) {
  const { locale: screenLocale } = useI18n();
  const screenT = useTranslate();
  const logoUrl = useStoreLogo();
  // The SHARED formatter, not a local copy. This was a verbatim duplicate of
  // the old `useMoney`, so when the till moved to Western digits and a named
  // currency the receipt silently kept printing `٣٢٠٫٠٠` — a bill that
  // disagreed with the screen it was printed from, which is the one place the
  // two must never differ.
  const screenFormat = useMoney();
  // The store's receipt language (owner, 2026-10-08), not the screen's.
  const locale = settings?.receiptLanguage ?? screenLocale;
  const sameLanguage = locale === screenLocale;
  const model = buildCustomerReceiptRenderModel({
    invoice,
    settings,
    tableName,
    locale,
    direction: locale === "en" ? "ltr" : "rtl",
    t: sameLanguage ? screenT : translatorFor(locale),
    formatMoney: sameLanguage ? screenFormat : moneyFormatter(locale),
    platformName,
    logoUrl,
  });
  return <CustomerReceiptTemplate {...model} />;
}

export type CustomerReceiptTemplateProps = {
  invoice: Invoice;
  settings: RestaurantSettings | null;
  tableName: string | null;
  /** T19 — the platform's name, for the band; a bill has only the id. */
  platformName?: string | null;
  locale: Locale;
  direction: "rtl" | "ltr";
  t: TranslateFn;
  formatMoney: (value: number) => string;
  paperWidth?: PaperWidth;
  action?: PrintArtifactAction;
  /** The store's logo for the head; a data URL when drawn for a printer. */
  logoUrl?: string | null;
};

export type CustomerReceiptRenderModel = CustomerReceiptTemplateProps & {
  paperWidth: PaperWidth;
  action: PrintArtifactAction;
};

export function buildCustomerReceiptRenderModel(
  input: CustomerReceiptTemplateProps,
): CustomerReceiptRenderModel {
  return {
    ...input,
    paperWidth: input.paperWidth ?? "80mm",
    action: input.action ?? "ORIGINAL",
  };
}

/** Pure receipt template shared by browser fallback and provider HTML. */
export function CustomerReceiptTemplate({
  invoice,
  settings,
  tableName,
  platformName = null,
  locale,
  direction,
  t,
  formatMoney: format,
  paperWidth = "80mm",
  action = "ORIGINAL",
  logoUrl = null,
}: CustomerReceiptTemplateProps) {
  // Positively a bill at no table — never "not dine-in", which an older
  // payload with no channel at all would satisfy.
  const offPremise =
    invoice.channel === "TAKEAWAY" || invoice.channel === "DELIVERY";
  /* D82: the two-value locale the footer rule takes. `locale` is the app's
     language tag; anything that is not English prints the Arabic footer. */
  const footerLocale = locale === "en" ? "en" : "ar";

  /*
   * Grouped, because a receipt is read by a customer: three teas are one line
   * reading "×3", not three lines (D15). The basket and un-accepted requests
   * are excluded — neither is something the customer has been served or asked
   * to pay for.
   */
  // B102 — who an off-premise bill is for, through the shared helper the panel
  // and the back office read, so the paper cannot name a customer differently
  // from the screen it was typed on.
  const customerLines = billCustomerLines(invoice);
  const billed = groupPlates(
    invoice.items.filter(
      (item) =>
        item.fulfillmentState !== "PENDING" &&
        item.fulfillmentState !== "DRAFT",
    ),
    format,
  );

  /*
   * The same status rule every other money surface follows: stored columns on
   * a finished bill, live from the plates while OPEN. No Print BUTTON exists
   * while a bill is open, but this component is always mounted and the print
   * stylesheet reveals it — so a browser-menu Ctrl+P on a live (or reopened)
   * table used to put "= 0.00" (or the old close's figures) under items that
   * carry prices. A provisional bill printed that way now states the live
   * truth instead.
   */
  const totals =
    invoice.status === "OPEN"
      ? liveOpenTotals(invoice)
      : {
          subtotal: invoice.subtotal,
          discountTotal: invoice.discountTotal,
          serviceChargeTotal: invoice.serviceChargeTotal,
          vatTotal: invoice.vatTotal,
          total: invoice.total,
        };

  // D146 — the line the discount at the bill prints (live while OPEN).
  const billDiscount = billDiscountLine(invoice);

  const headFacts: PaperFact[] = [
    orderFact(t, invoice.dailySequence, !offPremise ? tableName : null),
    {
      key: "check-in",
      label: t((m) => m.paper.checkIn),
      value: paperStamp(invoice.openedAt, settings?.timezone),
      ltr: true,
    },
    // Check out = when this paper is printed (owner, 2026-10-08).
    {
      key: "check-out",
      label: t((m) => m.paper.checkOut),
      value: paperStamp(new Date().toISOString(), settings?.timezone),
      ltr: true,
    },
    // D153 — the bill's captain, as the device receipt prints it.
    ...peopleFacts(t, invoice.captain?.name ?? null),
  ];
  const items: PaperItem[] = billed.map((group) => ({
    key: group.key,
    // One language per slip, like the reference.
    name: footerLocale === "en" && group.plateNameEn ? group.plateNameEn : group.plateNameAr,
    details: [
      group.comboLabel ? { text: group.comboLabel } : null,
      group.substitutionLabel ? { text: group.substitutionLabel } : null,
      footerLocale === "en" && group.addonLabelEn
        ? { text: group.addonLabelEn, ltr: true }
        : group.addonLabel
          ? { text: group.addonLabel }
          : null,
    ].filter((detail): detail is { text: string; ltr?: boolean } => detail !== null),
    quantity: group.quantity,
    unitPrice: group.quantity > 0 ? group.total / group.quantity : group.total,
    total: group.total,
    struck: group.fulfillmentState === "VOIDED",
  }));

  return (
    <div
      className="receipt customer-receipt"
      dir={direction}
      lang={locale}
      aria-hidden="true"
      data-print-artifact="CUSTOMER_RECEIPT"
      data-print-action={action}
      data-paper-width={paperWidth}
    >
      {action !== "ORIGINAL" ? (
        <div className="print-artifact-marker">
          {t((m) =>
            action === "REPRINT"
              ? m.printArtifact.reprint
              : action === "VOID"
                ? m.printArtifact.void
                : action === "REMAKE"
                  ? m.printArtifact.remake
                  : action === "REROUTE"
                    ? m.printArtifact.reroute
                    : m.printArtifact.change,
          )}
        </div>
      ) : null}
      <PaperHead
        storeName={settings?.receiptHeader ?? null}
        logoUrl={logoUrl}
        place={null}
        facts={headFacts}
      />

      {/* The tax registration number (D33) — on every copy, cheque included:
          a receipt that must carry one must carry it before payment too. */}
      {settings?.taxRegistrationNumber ? (
        <div style={{ textAlign: "center", fontSize: "9px" }}>
          {t((m) => m.invoices.taxRegistrationNumber)}:{" "}
          {/* Isolated LTR: a mixed pattern ("123/EG/456") inside an RTL
              paragraph reorders visually without it, and this prints. */}
          <bdi dir="ltr">{settings.taxRegistrationNumber}</bdi>
        </div>
      ) : null}

      {offPremise ? (
        /* T19 — THE BAND: the daily number large, then channel · platform ·
           platform order no., the three things a bag is matched by. */
        <div
          style={{ textAlign: "center", marginBottom: "1.5mm" }}
          data-testid="receipt-band"
        >
          <div style={{ fontSize: "24px", fontWeight: 900, lineHeight: 1.1 }}>
            #{invoice.dailySequence}
          </div>
          <div style={{ fontWeight: 700, fontSize: "12px" }}>
            {[
              channelLabel(invoice.channel, t),
              platformName,
              invoice.platformOrderRef ?? null,
            ]
              .filter((value): value is string => Boolean(value))
              .join(" · ")}
            {` · ${invoice.businessDate}`}
          </div>
        </div>
      ) : null}

      {/* WHO THIS ONE IS FOR (B9, B102) — on the paper, which is the copy that
          travels with the bag.

          The off-premise dialog has asked for these three since unit 4 and
          printed none of them. A rider was handed a receipt that said
          "Delivery" and nothing else; the address the cashier had typed was in
          the database and on no piece of paper in the building.

          Only on an off-premise bill, and only the lines that were filled in:
          a dine-in cheque has a table and needs none of this, and an empty
          "Address:" line on an 80mm roll is noise a customer has to read past. */}
      {customerLines.length > 0 ? (
        <div
          style={{
            marginBottom: "2mm",
            paddingBottom: "1.5mm",
            borderBottom: "1px dashed #000",
            fontSize: "11px",
          }}
        >
          {customerLines.map((line) => (
            <div key={line.key}>
              <strong>
                {line.key === "name"
                  ? t((m) => m.pos.customerName)
                  : line.key === "phone"
                    ? t((m) => m.pos.customerPhone)
                    : t((m) => m.pos.customerAddress)}
                :{" "}
              </strong>
              {/* Isolated, and the phone forced LTR: a number inside an RTL
                  paragraph reorders its own digits, and this one is dialled. */}
              <bdi dir={line.key === "phone" ? "ltr" : "auto"}>
                {line.value}
              </bdi>
            </div>
          ))}
        </div>
      ) : null}

      <PaperItems items={items} t={t} />

      {/*
        The subtotal a discount is taken OFF, and only when there is one.

        The lines above print each group at its CHARGED total — already net of
        the invoice's tier — so a discount row beneath them stated arithmetic
        that is false on paper: 127 − 28 = 127. The same false sum the till's
        totals block carried until the shared `listSubtotal` helper fixed it,
        and the receipt is the copy the customer keeps and queries, so it is
        the worse place to leave it. An undiscounted bill prints exactly as
        before: one total, no arithmetic to show.
      */}
      {totals.discountTotal > 0 || billDiscount > 0 ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>{t((m) => m.invoices.subtotal)}</span>
            <span>
              {format(
                listSubtotal({
                  subtotal: totals.subtotal,
                  discountTotal: totals.discountTotal,
                }),
              )}
            </span>
          </div>
          {totals.discountTotal > 0 ? (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>{t((m) => m.invoices.discountTotal)}</span>
              <span>−{format(totals.discountTotal)}</span>
            </div>
          ) : null}
          {/* D146 — the discount at the bill, its own «خصم» line, before the
              charges computed on what it left: subtotal − discount + service
              + VAT (+ fee) = total, on the copy the guest keeps. */}
          {billDiscount > 0 ? (
            <div
              style={{ display: "flex", justifyContent: "space-between" }}
              data-testid="receipt-bill-discount"
            >
              <span>{t((m) => m.billDiscount.lineLabel)}</span>
              <span>−{format(billDiscount)}</span>
            </div>
          ) : null}
        </>
      ) : null}

      {/*
        Service charge and VAT (D33) — a finished bill prints what it was
        closed with, an OPEN one the live figures (the `totals` branch above)
        — and each line NAMES ITS RATE from the invoice's snapshot: a customer
        (or an inspector) checking the arithmetic needs the percentage on the
        paper, not in the till's settings. Exclusive mode: the lines sit
        between the items and the total they add onto. Inclusive mode: the
        amounts are already inside every printed price, so the "منها" lines
        print AFTER the total they are a part of — a part cannot precede its
        whole on paper.
      */}
      {!invoice.pricesIncludeTax ? (
        <ChargeLines invoice={invoice} totals={totals} format={format} t={t} />
      ) : null}

      {/*
        THE DELIVERY FEE, on the copy the customer keeps.

        The till's own totals block has carried this row since B46 and the
        receipt did not, so a delivery bill printed "25.00 … VAT 3.50 … = 62.70"
        — 34.20 that the paper never names, on the one document a customer
        queries and an inspector reads. The lines above a total must visibly sum
        to it; that is the same rule the discount subtotal and the charge lines
        on either side of this one exist to keep.

        Fee and its VAT as ONE row, matching the till's `offPremiseFeeLabel`: a
        diner is quoted one delivery charge. They stay separate columns
        everywhere the money is REPORTED, so the courier's tax never spreads
        over the food — that is the reporting question, not the paper's.

        Printed in BOTH pricing modes and outside `ChargeLines`, because unlike
        the service charge and the VAT the fee is an addend of `total` in both:
        inclusive pricing carves the fee's tax out of the figure that was typed
        rather than folding it into the plate prices.
      */}
      {invoice.deliveryFee > 0 || invoice.deliveryFeeVat > 0 ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>{t((m) => m.pos.offPremiseFeeLabel)}</span>
            <span>{format(invoice.deliveryFee + invoice.deliveryFeeVat)}</span>
          </div>
          {/* T20 — the fee's own VAT, as a sub-line of the one row the diner
              is quoted: the paper then names every tax it carries. */}
          {invoice.deliveryFeeVat > 0 ? (
            <div
              style={{ display: "flex", justifyContent: "space-between", paddingInlineStart: "3mm", fontSize: "9px" }}
              data-testid="receipt-fee-vat"
            >
              <span>{t((m) => m.invoices.deliveryFeeVatIncluded)}</span>
              <span>{format(invoice.deliveryFeeVat)}</span>
            </div>
          ) : null}
        </>
      ) : null}

      <PaperTotal
        label={t((m) => m.paper.total)}
        value={format(totals.total)}
        /* Nothing beside the total (owner, 2026-10-08). An unpaid copy still
           says so on its own line below (UX-round D25). */
        status={null}
      />

      {invoice.pricesIncludeTax ? (
        <ChargeLines invoice={invoice} totals={totals} format={format} t={t} />
      ) : null}

      {/* T17 — THE RIDER BLOCK, at the FOOT of the copy that travels with the
          bag (paper persona, 2026-10-07: §5.11 says the foot, and a rider reads
          the paper bottom-up at the door): what to collect, large and LTR, with
          the phone, the first address line and the bill number. On a
          platform's order (the platform took the money) and on a bill paid in
          full there is nothing to collect, so no headline prints at all
          (founder, 2026-10-08) — only those facts — and a rider never collects
          twice. A
          DELIVERY only — a takeaway is collected at the counter by the
          customer, who reads the total — and never a CANCELLED one, which
          nobody is collecting for. */}
      {invoice.channel === "DELIVERY" && invoice.status !== "CANCELLED" ? (
        <RiderBlock
          invoice={invoice}
          total={totals.total}
          format={format}
          t={t}
        />
      ) : null}

      {/*
        How it was paid, and the change — on the SETTLED copy only.

        The cheque is printed before any of this is known, so it carries neither
        line; printing "طريقة الدفع: —" on the copy the customer is about to pay
        against would be noise at best. Change due is shown only for CASH and
        only when it is positive: a card sale has no change by definition, a
        write-off (D31) took no money, and an exact-cash sale printing "الباقي 0"
        invites a customer to ask what the zero means.
      */}
      {/*
        NO "NOT PAID YET" MARK (founder, 2026-10-08, overruling UX-round D25).
        The pre-payment print is the bill the customer reads and then pays
        from, on every channel; telling that customer it is unpaid is wrong.
        What tells a paid copy apart is the SETTLED block below — the method
        (or "method not recorded"), when it was paid, and the change.
      */}
      {invoice.status === "SETTLED" ? (
        /* THE PAID COPY (owner, 2026-10-11): "Status : Paid" first, then how
           and when — in the paper's own "label : value" rows. B117/B1: the
           method names every tender of a bill paid two ways; UX-round D25: a
           settled bill with no stored method says so rather than going quiet;
           D24: the clock is the STORE's, in Western digits (`paperStamp`, so
           an Arabic slip cannot reorder it). */
        <div style={{ marginTop: "1.5mm" }} data-testid="receipt-paid">
          <PaperSumRow
            label={t((m) => m.paper.status)}
            value={<strong>{t((m) => m.paper.statusPaid)}</strong>}
            testId="receipt-paid-status"
          />
          <PaperSumRow
            label={t((m) => m.pos.settlementMethod)}
            value={
              invoice.settlementMethod
                ? tenderMethodsLabel(
                    t,
                    invoice.tenders.map((tender) => tender.method),
                    invoice.settlementMethod,
                  )
                : t((m) => m.pos.receiptMethodNotRecorded)
            }
            testId="receipt-method"
          />
          {invoice.settledAt ? (
            <PaperSumRow
              label={t((m) => m.pos.receiptSettledAtLabel)}
              value={paperStamp(invoice.settledAt, settings?.timezone)}
              testId="receipt-settled-at"
            />
          ) : null}
          {invoice.settlementMethod === "CASH" &&
          invoice.settlementAmount !== null &&
          invoice.settlementAmount - invoice.total > 0 ? (
            <PaperSumRow
              label={t((m) => m.pos.changeDue)}
              value={format(invoice.settlementAmount - invoice.total)}
            />
          ) : null}
        </div>
      ) : null}

      {/* D82 — THIS slip's own language. One stored footer used to print under
          both, so an Arabic line ended an English receipt. The choice, and the
          fall back to the Arabic when a store has written no English one, is
          `receiptFooterForLocale` — one tested rule, because there are two
          customer-facing slips and a rule copied into both diverges. */}
      <PaperFooter
        text={receiptFooterForLocale(settings, footerLocale)}
        poweredBy={t((m) => m.paper.poweredBy)}
        testId="receipt-footer"
      />
    </div>
  );
}

export async function renderCustomerReceiptDocument(
  props: CustomerReceiptTemplateProps & {
    paperWidth: PaperWidth;
    hardwareActions?: PrintHardwareActions;
  },
): Promise<StandalonePrintArtifact> {
  const model = buildCustomerReceiptRenderModel(props);
  return await renderStandalonePrintArtifact({
    kind: "CUSTOMER_RECEIPT",
    action: model.action,
    locale: model.locale,
    paperWidth: model.paperWidth,
    title: `CUSTOMER_RECEIPT #${model.invoice.dailySequence}`,
    content: <CustomerReceiptTemplate {...model} />,
    hardwareActions: props.hardwareActions,
  });
}

/**
 * The two charge lines, each naming its snapshotted rate.
 *
 * The rate prints from the INVOICE's snapshot (never the live settings), so a
 * reprint after a rate change still states the percentage this bill was
 * actually charged at.
 */
function ChargeLines({
  invoice,
  totals,
  format,
  t,
}: {
  /** Rates and mode come from the invoice's snapshot… */
  invoice: Invoice;
  /** …amounts from the status-branched totals (live while OPEN). */
  totals: { total: number; subtotal: number; serviceChargeTotal: number; vatTotal: number };
  format: (value: number) => string;
  t: TranslateFn;
}) {
  if (totals.serviceChargeTotal <= 0 && totals.vatTotal <= 0) {
    return null;
  }
  return (
    <>
      {/*
        DEF-6 — inclusive mode only. The lines below name a SHARE of the
        total ("منها…"); without the whole they are a share of, nothing on
        the paper says what the customer's 100 actually bought. This is the
        goods value, not "the taxable amount" — when `vatOnServiceCharge` is
        on, VAT is charged on net + service charge, not on this figure alone,
        so labelling it as the VAT base would contradict the VAT line right
        under it. Gated strictly on `pricesIncludeTax`: exclusive mode's
        `subtotal` already IS the net and already prints above these lines,
        so a second net line there would just repeat it.
      */}
      {invoice.pricesIncludeTax ? (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>{t((m) => m.invoices.netBeforeCharges)}</span>
          <span>{format(netBeforeCharges(totals))}</span>
        </div>
      ) : null}
      {totals.serviceChargeTotal > 0 ? (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>
            {t(
              (m) =>
                invoice.pricesIncludeTax
                  ? m.invoices.serviceChargeIncludedWithRate
                  : m.invoices.serviceChargeWithRate,
              { percent: String(Number(invoice.serviceChargePercent ?? 0)) },
            )}
          </span>
          <span>{format(totals.serviceChargeTotal)}</span>
        </div>
      ) : null}
      {totals.vatTotal > 0 ? (
        <div style={{ display: "flex", justifyContent: "space-between" }} data-testid="receipt-vat-line">
          {/* "VAT 14%" — the rate, not its base (owner, 2026-10-10). */}
          <span>
            {t(
              (m) =>
                invoice.pricesIncludeTax
                  ? m.invoices.vatIncludedWithRate
                  : m.invoices.vatWithRate,
              { percent: String(Number(invoice.vatPercent ?? 0)) },
            )}
          </span>
          <span>{format(totals.vatTotal)}</span>
        </div>
      ) : null}
    </>
  );
}

/**
 * T17 — the rider block on the browser receipt; the device artifact draws the
 * same block from `riderCollect`, so the two papers cannot disagree about what
 * a rider collects. `tenderedTotal` is the ledger's paid position.
 */
function RiderBlock({
  invoice,
  total,
  format,
  t,
}: {
  invoice: Invoice;
  total: number;
  format: (value: number) => string;
  t: TranslateFn;
}) {
  const rider = riderCollect({
    onPlatform: invoice.aggregatorId !== null,
    total,
    paidTotal: invoice.tenderedTotal,
  });
  const facts = [
    invoice.customerPhone ? `\u2066${invoice.customerPhone}\u2069` : null,
    invoice.channel === "DELIVERY" ? firstAddressLine(invoice.deliveryAddress) : null,
    // Isolated like the phone: a bare «#41» after an Arabic street joins the
    // RTL run and prints as «41#» (paper persona, 2026-10-07).
    `\u2066#${invoice.dailySequence}\u2069`,
  ].filter((value): value is string => Boolean(value));
  return (
    <div
      style={{ border: "2px solid #000", padding: "1.5mm", margin: "2mm 0 0", textAlign: "center" }}
      data-testid="receipt-rider"
    >
      {rider.kind === "collect" ? (
        <>
          <div style={{ fontSize: "11px", fontWeight: 800 }}>
            {t((m) => m.pos.receiptToCollect)}
          </div>
          <div
            dir="ltr"
            style={{ fontSize: "22px", fontWeight: 900, lineHeight: 1.1 }}
            data-testid="receipt-rider-amount"
          >
            {format(rider.amount)}
          </div>
        </>
      ) : null}
      {/* Nothing to collect (a platform's order, or a bill paid in full): no
          headline at all (founder, 2026-10-08) — only the facts the courier
          still needs at the door. */}
      <div
        style={{ fontSize: "11px", marginTop: rider.kind === "collect" ? "1mm" : 0 }}
        data-testid="receipt-rider-facts"
      >
        {facts.join(" · ")}
      </div>
    </div>
  );
}
